/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

export const USER_MANAGEMENT_TOOL_TIPS = {
	ACTIVE_DIRECTORY_ID: "The ID of the user as shown in Azure Active Directory.",
	EMAIL: "The email of the user. This should match the email in cognito.",
	ROLE_NAME: "The friendly name of the role that will appear when assigning the role to an agent.",
};

export const CONFIG_TOOL_TIPS = {
	ENABLE_TEAMS_STATUS_SYNC:
		"Enable status syncing from Microsoft Teams to PACE for agents with Azure Active Directory IDs.",
	ENABLE_CONNECT_TO_TEAMS_STATUS_SYNC:
		"Enable status syncing from PACE to Microsoft Teams for agents with Azure Active Directory IDs.",
	ENABLE_TEAMS_STATUS_AGENT_SYNC:
		"This will enable the syncing of statuses from Microsoft Teams to Amazon Connect for the selected user.",
	TEAMS_SYNC_TENANT_ID: "This is the Tenant ID from the Azure Active Directory application for PACE.",
	TEAMS_SYNC_CLIENT_ID: "This is the Client ID from the Azure Active Directory application for PACE.",
	TEAMS_SYNC_CLIENT_SECRET: "This is a Client Secret generated in the Azure Active Directory application for PACE.",
	TEAMS_SYNC_CLIENT_SECRET_EXPIRY_DATE: "This is the Expiry date of the generated Client Secret.",
	TEAMS_SYNC_USERNAME:
		"Azure AD Username is a username of a service account user that is needed for retrieving user statuses from Microsoft Teams.",
	TEAMS_SYNC_USER_PASSWORD:
		"Azure AD User password is a password of a service account user that is needed for retrieving user statuses from Microsoft Teams.",
	TEAMS_ACTIVITIES_TO_OFFLINE_STATUS:
		"Select the Microsoft Teams status Activities that will reflect as Offline statuses in PACE. All Microsoft Teams status Activities will default to Offline in PACE except for Available.",
	AWS_CONNECT_TO_TEAMS_OFFLINE_STATUS:
		"Select the PACE Agent States that will be reflected as a Busy status in Microsoft Teams.",
};

export const SOCIAL_MEDIA_TOOL_TIPS = {
	PLATFORM: "The platform that this integration will be designed to interact with.",
	FRIENDLY_NAME: "A friendly name for the integration that can be searched on.",
	META_VALIDATION_TOKEN: "The token that will be used to validate the contact endpoint by Meta.",
	META_APPLICATION_ID: "The ID of the Meta application to integrate.",
	META_APPLICATION_SECRET:
		"The Application secret of the Meta application. Once this is saved only the last five digits will be visible on this page.",
	PAGES: "The pages setup to interact with your application in Meta.",
	META_PAGE_ID: "The ID of the Facebook page or Instagram account.",
	META_CALLBACK_URI: "The callback URI needed by Meta to contact PACE.",
	META_PAGE_SECRET_TOKEN:
		"The Secret Token generated for the Facebook or Instagram page ID generated in the Meta Application Messages settings page.",
};

export const AZURE_BOT_TOOL_TIPS = {
	AZURE_BOT_CALLBACK_URI: "The callback URI needed by a Teams application to contact PACE.",
	BOT_PASSWORD: "The password generated for the Azure bot.",
	BOT_ID: "The ID for the Azure Bot.",
	FRIENDLY_NAME: "A friendly name for the Teams application that can be searched on.",
};

export const PINPOINT_TOOL_TIPS = {
	PROJECT_ID: "The Pinpoint project that the phone number belongs to.",
	PHONE_NUMBER:
		"The phone number that a customer will use to contact the call center. This number needs to exactly match the number at the top of the AWS console page for the pinpoint number. Ex: '+18001234567'",
	FRIENDLY_NAME: "A friendly name for the Pinpoint application that can be searched on.",
	CREATE_OUTBOUND_NOTIFICATION_CONTACT_RECORD:
		"If this is checked: A CTR record will be created in Amazon Connect for each notification that is sent via the 'Send SMS' tab on the Agent Console ",
};

export const EMAIL_TOOL_TIPS = {
	PROJECT_ID: "The Email project that the email address belongs to.",
	EMAIL_ADDRESS: "The email address that a customer will use to contact the call center. Ex: 'agent@company.com'",
	FRIENDLY_NAME: "A friendly name for the SES application that can be searched on.",
	CREATE_OUTBOUND_NOTIFICATION_CONTACT_RECORD:
		"If this is checked: A CTR record will be created in Amazon Connect for each notification that is sent via the 'Send SMS' tab on the Agent Console ",
};

export const OUTBOUND_SMS_NOTIFICATION_TOOL_TIPS = {
	ORIGIN_PHONE_NUMBER: "The phone number the SMS message will be sent from.",
	DESTINATION_PHONE_NUMBER: "The destination number for the SMS message.",
};

export const LEX_BOT_TOOL_TIPS = {
	NAME:
		"The name of a Lex bot must be unique, contain between 1 and 45 characters, and consist of only English letters and non-consecutive underscores." +
		" NOTE: Changing the name of a Lex Bot will disassociate the Lex Bot from Connect.",
	DESCRIPTION: "A brief message describing what this Lex Bot is used for.",
	IDLE_SESSION_TTL:
		"The number of minutes before the customer is automatically sent the abort statement and disconnected from the Lex Bot.",
	VOICEID: "The voice Lex Bot will use when a Lex Bot is used on a call. Voices are language specific.",
	LANGUAGE:
		"The language the Lex Bot will use when interpreting messages. This should be the same as your webchat agent language." +
		" NOTE: Changing the language of a Lex Bot will disassociate the Lex Bot from Connect.",
	COPPA_COMPLIANCE:
		"Lex Bot will be used in a manner that would be subject to COPPA (Childrens Online Privacy Protection Act)." +
		" Lex Bots that are subject to COPPA do not store or retain voice or text information, which can limit improvement of bot performance over time.",
	CLARIFICATION_MESSAGES:
		"These are the messages that will be returned to the customer when the Lex Bot is unable to determine an intent from a message." +
		" If a Lex Bot cannot determine an intent with confidence, it will return messages to the customer to prompt them to clarify their question." +
		" \nMessages will be sent out starting with all messages in group 1 and incrementing group numbers with each attempt." +
		" If an intent cannot be established before the max attempts have been reached, the customer will be sent the abort statement and passed to the error path in the Connect contact flow block.",
	ABORT_MESSAGES:
		"These are the messages that will be returned to the customer when the Lex Bot has been unable to determine an intent for the set amount of max attempts." +
		" After these messages are sent to the customer, the customer is then placed in the error branch of the Connect contact flow block.",
	INTENT_SETTINGS:
		"These are the intents that you want to be available in Connect for use in conjunction with this Lex Bot." +
		" On the left are all of the intents that are associated with this Lex Bot. On the right are all other available intents." +
		" To create an intent, navigate to the Intents page in the Lex Bot section of the Pace Admin website." +
		" NOTE: Two intents on the same Lex Bot cannot share a sample phrase.",
	INTENT_SAMPLE_PHRASES:
		"A list of phrases used to better inform the lex bot as to when this intent should be selected for a user. To use slot types in these phrases place the slot type name in brackets inside of a sample phrase.",
	INTENT_DELETE_DISABLED:
		"To delete this intent you must first remove it from all Lex Bots and allow the bots to rebuild.",
	INTENT_RELATED_BOTS:
		"This is a list of bots which currently reference this intent. Here you can also see which bots are associated with Connect.",
	SLOT_TYPE_RELATED_INTENTS: "Intents that currently reference this slot type",
	SLOT_VALUE: "The list of expected values for the slot.",
	INTENT_SLOTS:
		"An intent can require zero or more slots or parameters. The user must provide values for all required slots before Amazon Lex can fulfill the intent.",
	INTENT_SLOTS_NAME:
		"The name of a Slot must be unique, contain between 1 and 100 characters, and consist of only English letters and non-consecutive underscores, dashes, and periods.",
	INTENT_SLOTS_SLOT_TYPE:
		"The type of the Slot defines how the Lex Bot processes the information available in the Slot. Built-in Slot Types are prefixed with Amazon, and custom Slot Types are prefixed with PACE.",
	INTENT_SLOTS_PROMPT:
		"The prompt used by the Lex Bot to gather the required Slot value from the customer if it has not already been provided.",
	INTENT_SLOTS_REQUIRED:
		"Specifies whether the Slot is required or optional. If a Slot is required, the customer will be prompted by the Lex Bot to provide its value.",
	INTENT_SLOTS_PRIORITY: "The order in which the Lex Bot will prompt the customer for the Slot values.",
	SLOT_TYPE_NAME:
		"The name of a Slot Type must be unique, contain between 1 and 100 characters, and consist of only English letters and non-consecutive underscores.",
	SLOT_TYPE_DESCRIPTION: "A brief message describing what this Slot Type is used for.",
};

export const WEB_CHAT_TOOL_TIPS = {
	INSTANCEID: "The Instance ID for the Amazon Connect instance PACE is connected to.",
	CONTACT_FLOW: "The contact flow the customer will be routed to in Connect.",
	ALLOWED_ORIGINS:
		"The Origins from which the web chat widget will be allowed to initiate web chats. At least one origin is required.",
	FRIENDLY_NAME: "The friendly name for this configuration. Used for logging purposes and ease of use.",
	PROMPT_FOR_USERNAME_CHECKBOX:
		"This will enable a username prompt to be displayed when a customer opens the chat window for the first time if a username is not already set through the widget API.",
	PROMPT_FOR_USERNAME_MESSAGE:
		"The message displayed to the customer on first chat session to prompt for a username. If this field is empty and the username prompt is enabled; a default message will be used.",
	USERNAME_ENTRY_THANK_YOU_CHECKBOX:
		"This will enable an automatic reply message to be displayed after a username has been entered.",
	USERNAME_ENTRY_THANK_YOU_MESSAGE:
		"The reply message to the customer after they have entered their username. You can include the entered username in your custom message by including the tag '{userName}' in your message. If this field is empty and the automatic reply is enabled; a default message will be used.",
	ENABLE_AUTOMATIC_TRANSLATIONS_CHECKBOX:
		"This will enable the webchat to attempt to automatically translate web chat messages from a customer's selected language to the selected agent language.",
	AGENT_LANGUAGE_SELECT:
		"The language that the web chat will translate to when sending messages to an agent. Will only translate messages if automatic translations are enabled.",
	ENABLED_TRANSLATION_LANGUAGES_SELECT:
		"The list of translation languages that will be available to choose from in the web chat widget.",
	HEADER_BACKGROUND_COLOR: "The background color of the banner on the top of the open web chat.",
	CLOSE_BUTTON_IMAGE:
		"The image displayed on the top right of the chat window header to close the web chat. If this field is empty a default image will be used.",
	HEADER_BACKGROUND_IMAGE:
		"The image used as the chat header background, it will overwrite the header background color setting. Image will be centered and cropped to fit the available space.",
	HIDE_BUTTON_IMAGE:
		"The image displayed on the top of the chat window header to minimize the webchat. if this field is empty a default image will be used.",
	EXPAND_BUTTON_IMAGE:
		"The image displayed on the top of the chat window header to maximize the webchat. if this field is empty a default image will be used.",
	COLLAPSE_EXPAND_BUTTON_IMAGE:
		"The image displayed on the top of the chat window header to minimize the webchat. if this field is empty a default image will be used.",
	TITLE_TEXT: "The title displayed on the top of the open web chat.",
	TITLE_TEXT_COLOR: "The color of the text for the title.",
	SUBTITLE_TEXT_COLOR: "The color of the text for the subtitle.",
	CONNECTING_MESSAGE_TEXT_COLOR:
		"The color of the text for the message displayed while customer is connected to agent.",
	LANGUAGE_BUTTON_IMAGE_URL:
		"The image to be displayed that when clicked will display a list of all available languages. If this field is empty a default image will be used.",

	START_CHAT_IMAGE:
		"The image displayed on the initial screen to open the web chat. If this field is empty a button is displayed to open a web chat.",
	START_BUTTON_TEXT: "The text to be displayed on the start button to open the web chat.",
	START_BUTTON_BACKGROUND_COLOR: "The background color for the start button.",
	START_BUTTON_TEXT_COLOR: "The color of the text for the start button.",
	HANG_UP_BUTTON_TEXT: "The text to be displayed on the hang up button to close the web chat.",
	HANG_UP_BUTTON_BACKGROUND_COLOR: "The background color for the hang up button",
	HANG_UP_BUTTON_TEXT_COLOR: "The color of the text for the hang up button.",
	RESUME_BUTTON_TEXT: "The text to be displayed on the resume button to resume the web chat.",
	RESUME_BUTTON_BACKGROUND_COLOR: "The background color for the resume button.",
	RESUME_BUTTON_TEXT_COLOR: "The color of the text for the resume button.",
	RESPONSE_MESSAGE_BACKGROUND_COLOR: "The background color of a message from the contact center agent.",
	RESPONSE_MESSAGE_TEXT_COLOR: "The text color of a message from the contact center agent.",
	USER_MESSAGE_BACKGROUND_COLOR: "The background color of a message from the customer.",
	USER_MESSAGE_TEXT_COLOR: "The text color of a message from the customer.",
	MESSAGE_VIEWER_BACKGROUND_COLOR: "The background color of the chat area.",
	NEW_MESSAGE_BACKGROUND_COLOR: "The background color of new customer message area.",
	NEW_MESSAGE_TEXT_COLOR: "The text color of the new customer message area.",
	SEND_MESSAGE_BUTTON_IMAGE:
		"The image used as a button that when clicked will send the message to the contact center. If this field is empty a default image will be used.",
	SYSTEM_MESSAGE_LABEL: "User friendly label to show in chat for system messages.",
	BOT_MESSAGE_LABEL: "User friendly label to show in chat for BOT messages.",
	USER_NOTIFICATION_URL:
		"The audio used as a notification every time a new message is received. If the field is empty a default sound will be used.",
	ENABLE_AUDIO_NOTIFICATION: "This will enable the audio notification when a new message is received.",
	MUTE_MENU_BUTTON_IMAGE:
		"The image for the button used to mute audio notification. If this field is empty a default image will be used.",
	UNMUTE_MENU_BUTTON_IMAGE:
		"The image for the button used to unmute audio notification. If this field is empty a default image will be used.",
	ENABLE_SURVEYS_CHECKBOX:
		"This setting controls whether or not a survey is presented to the user at the end of an interaction.",
	ALWAYS_OFFER_SURVEYS_CHECKBOX:
		"By default, the survey will only be offered after an interaction with an agent. When this setting is enabled, the survey will be offered at all times regardless of whether the user was connected to an agent or not.",
	SURVEY_QUESTION: "The question displayed to the customer as a part of the after chat survey.",
	SURVEY_FOLLOW_UP_QUESTION:
		"This question will be asked as a follow up if the customer answers with one of the given values.",
	SURVEY_SELECTED_RATING_IMAGE:
		"The image used as a button to show which levels of ratings a customer has selected. If this field is empty a default image will be used.",
	SURVEY_UNSELECTED_RATING_IMAGE:
		"The image used as a button to show which levels of ratings a customer has not selected. If this field is empty a default image will be used.",
	SENDING_ATTACHMENTS_PHRASE:
		"This phrase is shown to the customer while the attachments are uploading. If this field is empty a default value will be used.",
	SURVEY_INTRODUCTION_MESSAGE:
		"This message is sent to the customer when the agent ends the conversation and the survey starts. If this field is empty a default message will be used.",
	SURVEY_COMPLETE_MESSAGE:
		"This message is sent to the customer when they have completed all survey questions. If this field is empty a default message will be used.",
	ENABLE_ATTACHMENTS_CHECKBOX: "Checking this box will enable customers to send attachments to amazon connect.",
	ATTACHMENT_CHIP_TEXT_COLOR: "This is the color of the text for the attachment list chips.",
	ATTACHMENT_CHIP_BACKGROUND_COLOR: "This is the color of the background for the attachment list chips.",
	ATTACHMENT_BUTTON_IMAGE:
		"This is the image for the button that when clicked will allow customers to upload attachments. If this field is empty a default image will be used.",
	ATTACHMENT_WARNING_IMAGE:
		"This is the image displayed on the attachment chip when there is a warning for the attachment. If this field is empty a default image will be used.",
	DOWNLOAD_ATTACHMENT_IMAGE:
		"This is the image displayed on messages from the agent containing attachments. When clicked this will trigger a download of the attached file. If this field is empty a default image will be used.",
	ENABLE_DOWNLOAD_BUTTON_CHECKBOX: "This will enable the download button.",
	DOWNLOAD_BUTTON_IMAGE_URL:
		"The image to be displayed that when clicked, it will download the chat transcript. If this field is empty a default image will be used.",
	LANGUAGE_TEXT_COLOR: "The color of the selected language label.",
	CONVERSATION_SEPARATOR_COLOR:
		"The color of the separator line displayed between conversations with the same customer.",
	WIDGET_VERTICAL_ALIGNMENT: "The vertical position of the widget on the page.",
	WIDGET_HORIZONTAL_ALIGNMENT: "The horizontal position of the widget on the page.",
	WIDGET_MARGIN:
		"The margin around the chat widget and buttons. This field allows raw CSS to be passed in. EX: '20px', '20% 10%', and '20px 10px 20px 10px' (without quotes) would be valid inputs.",
	PLACEHOLDER_TEXT: "The text to be displayed as a placeholder in the send message input area.",
	PLACEHOLDER_COLOR: "The color of the placeholder text to be displayed in the send message input area.",
	INTERACTIVE_MESSAGE_BUTTON_TEXT_COLOR: "The color of the text for the interactive message button.",
	INTERACTIVE_MESSAGE_BUTTON_BACKGROUND_COLOR: "The background color for the interactive message button.",
	CHAT_ESCALATIONS_ENABLE_FEATURE: "Enable Audio calls/Video calls/Screen sharing",
	ESCALATION_OPTIONS_BUTTON_IMAGE_URL:
		"When clicked, this image displays meeting start/stop controls. If this image is empty, a default image will be used",
	ESCALATION_OPTIONS_TEXT_COLOR: "The color of the Escalation options text.",
	ESCALATION_WAITING_MESSAGE_TEXT_COLOR: "The color of the Waiting message text at bottom of widget.",
	ESCALATION_VIDEO_BACKGROUND_COLOR: "Meeting screen background color",
	LANGUAGE_TRANSLATOR_LANGUAGES_LOOKUP: "Select the translation language.",
};

export const CALL_ME_NOW_TOOL_TIPS = {
	INSTANCEID: "The Instance ID for the Amazon Connect instance PACE is connected to.",
	CONTACT_FLOW: "The contact flow the outbound call will use in Connect.",
	ALLOWED_ORIGINS:
		"The Origins from which the call me now widget will be allowed to initiate outbound call. At least one origin is required.",
	CONFIG_NAME: "The friendly name for this configuration. Used for logging purposes and ease of use.",
	SOURCE_PHONE_NUMBER: "The source phone number from which the call will be made.",
	TITLE_TEXT: "The title displayed on the top of the open call me now widget.",
	TITLE_TEXT_COLOR: "The color of the text for the title.",
	HEADER_BACKGROUND_COLOR: "The background color of the banner on the top of the open call me now widget.",
	OPTIONS_LABEL_TEXT: "The label displayed for accepting the options for call.",
	OPTIONS_LABEL_TEXT_COLOR: "The color of the text for the label.",
	TO_NUMBER_LABEL_TEXT: "To number label text.",
	TO_NUMBER_LABEL_TEXT_COLOR: "The color of the text for the label.",
	CALL_BUTTON_TEXT: "Call me now button caption text.",
	CALL_BUTTON_BACKGROUND_COLOR: "Call me now button background color.",
	CALL_BUTTON_DISABLED_BACKGROUND_COLOR: "Call me now button background color when the button is disabled.",
	CALL_BUTTON_TEXT_COLOR: "Call me now button caption color.",
	CALL_BUTTON_DISABLED_TEXT_COLOR: "Call me now button caption color when the button is disabled.",
	ENABLED_CALL_OPTIONS: "Enable call options selector in the widget.",
	SUBTITLE_TEXT_COLOR: "The color of the text for the subtitle.",
	WIDGET_BACKGROUND_COLOR: "The color of the widget background.",
	TO_NUMBER_PLACEHOLDER_TEXT: "The placeholder text for the to number format.",
	TO_NUMBER_VALIDATION_MESSAGE: "The validation message to display for number format mismatch.",
	VALIDATION_MESSAGE_TEXT_COLOR: "The color of the validation message.",
	CALL_SUCCESS_MESSAGE: "The message which will be displayed when call is succeeded.",
	CALL_FAILURE_MESSAGE: "The message which will be displayed when call is failed.",
	HEADER_BACKGROUND_IMAGE:
		"The image used as the widget header background, it will overwrite the header background color setting. Image will be centered and cropped to fit the available space.",
	WIDGET_BACKGROUND_IMAGE:
		"The image used as the widget background, it will overwrite the background color setting. Image will be centered and cropped to fit the available space.",
};

export const CANNED_RESPONSE_TOOLTIPS = {
	CATEGORY_NAME:
		"The category name is required, must be unique regardless of casing, and cannot exceed 50 characters in length.",
	RESPONSE_NAME: "The response name is required and cannot exceed 50 characters in length.",
	TAGS: "Tags which can be used to search for this response in the agent console.",
};
export const SCHEDULED_REPORT_TOOLTIPS = {
	REPORT_FILE_NAME: "Report path must match the file name in the scheduled report",
	REPORT_EMAIL_ADDRESS: "You can add up to 5 email addresses",
};

export const EXTERNAL_CONTACT_TOOLTIPS = {
	CONTACT_NAME: "Full name of the contact.",
	CONTACT_PHONE:
		"Contact phone number in E.164 format. E.164 numbers are formatted [+] [country code] [subscriber number including area code] and can have a maximum of fifteen digits. e.g. +14155552671.",
	CONTACT_TAGS: "You can add up to three tags to help with searching and categorizing contacts.",
	CONTACT_AZURE_AD_USER_ID:
		"The ID of the user as shown in Azure Active Directory. Used to display MS Teams status for this contact in the Agent Console.",
	AZURE_GROUPS_AUTOCOMPLETE: "Filter users by Azure AD group.",
};

export const UPDATE_CONTACT_TOOLTIPS = {
	SEARCH_CONTACT_ID_TOOLTIP: "Insert the Contact ID of the contact you would like to update.",
};

export const AGENT_STATUS_ALERT_TOOLTIPS = {
	ALERT_MESSAGE_TOOLTIP: "This message will be displayed when the specified time has passed.",
	ALERT_TIMEOUT_TOOLTIP: "The specified time interval after which the message will be shown.",
	ALERT_DISABLE_HINT_TOOLTIP: "Alert messages will not be shown on agent console when disabled.",
};

export const DASHBOARDS_CONFIG_TOOLTIPS = {
	REPORT_NAME_FIELD:
		"The name of a Dashboard must be unique, maximum of 50 characters, and consist of only alphanumeric characters, underscores, and spaces.",
	URL_FIELD: "Use the dashboard URL or iFrame embed code copied from OpenSearch Dashboards.",
	HIDDEN: "If dashboard is hidden, it will not be shown on dashboards navigation.",
};

export const PROMPTS_EDIT_TOOLTIP = {
	PROMPT_ID_FIELD: "Enter the Prompt ID",
	TYPE_FIELD: "Select type of prompt message",
	PROMPT_FIELD: "Enter prompt message",
};

export const QUEUE_CALLBACKS_CONFIG_TOOLTIPS = {
	ENABLED_FIELD: "Toggle callbacks for this queue. When disabled, any additional checks for callbacks are skipped.",
	MIN_EWT_FIELD: "The minimum estimated wait time before callbacks are offered, set to 0 to ignore this setting.",
	MIN_QUEUED_COUNT_FIELD:
		"The minimum number of contacts in the queue before callbacks are offered, set to 0 to ignore this setting.",
	RETRIES_FIELD: "The number of times a callback should be retried if the customer did not answer after one attempt.",
	RETRY_TIMEOUT_FIELD: "The amount of time before a missed callback is retried.",
};
